import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { OPERATION_TYPES, tourDeparture } from "../lib/constants";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight, Route as RouteIcon, ClipboardList, CalendarDays, Clock } from "lucide-react";

const DAY_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function mondayOf(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = lunedi
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function iso(d) { return new Date(d).toISOString().slice(0, 10); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function Calendar() {
  const { user } = useAuth();
  const canDrag = ["responsabile_logistica", "amministratore", "pm_produzione"].includes(user?.role);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [requests, setRequests] = useState([]);
  const [tours, setTours] = useState([]);
  const [dragOver, setDragOver] = useState(null);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const load = useCallback(async () => {
    try {
      const [r, t] = await Promise.all([api.get("/requests"), api.get("/tours")]);
      setRequests(r.data);
      setTours(t.data);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDrop = async (dayIso, e) => {
    e.preventDefault();
    setDragOver(null);
    const raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    let parsed;
    try { parsed = JSON.parse(raw); } catch { return; }
    const { type, id, cur } = parsed;
    if (cur === dayIso) return;
    try {
      if (type === "request") await api.patch(`/requests/${id}/reschedule`, { date: dayIso });
      else await api.patch(`/tours/${id}/reschedule`, { date: dayIso });
      toast.success("Spostato al " + new Date(dayIso).toLocaleDateString("it-IT"));
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const reqByDay = (dayIso) => requests.filter((r) => (r.deadline || "").slice(0, 10) === dayIso);
  const tourByDay = (dayIso) => tours.filter((t) => t.date === dayIso);
  const undated = requests.filter((r) => !r.deadline);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-lm-text flex items-center gap-2"><CalendarDays className="w-7 h-7 text-lm-sand" /> Calendario settimanale</h1>
          <p className="text-sm text-lm-muted">Trascina richieste e giri per spostarli tra i giorni</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="cal-prev" onClick={() => setWeekStart((w) => addDays(w, -7))} className="border-lm-sand text-lm-brown"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" data-testid="cal-today" onClick={() => setWeekStart(mondayOf(new Date()))} className="border-lm-sand text-lm-brown">Oggi</Button>
          <Button variant="outline" data-testid="cal-next" onClick={() => setWeekStart((w) => addDays(w, 7))} className="border-lm-sand text-lm-brown"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
        {days.map((d, i) => {
          const dIso = iso(d);
          const isToday = dIso === iso(new Date());
          return (
            <div key={dIso} data-testid={`cal-day-${dIso}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(dIso); }}
              onDragLeave={() => setDragOver((x) => (x === dIso ? null : x))}
              onDrop={(e) => onDrop(dIso, e)}
              className={`lm-card p-2.5 min-h-[220px] flex flex-col transition-colors ${dragOver === dIso ? "ring-2 ring-lm-brown bg-lm-sandsoft/50" : ""}`}>
              <div className={`text-center pb-2 mb-2 border-b ${isToday ? "border-lm-brown" : "border-lm-sandsoft"}`}>
                <p className="text-[11px] uppercase tracking-wider text-lm-muted font-bold">{DAY_LABELS[i]}</p>
                <p className={`font-display text-lg ${isToday ? "text-lm-brown font-bold" : "text-lm-text"}`}>{d.getDate()}/{d.getMonth() + 1}</p>
              </div>
              <div className="space-y-2 flex-1">
                {tourByDay(dIso).map((t) => (
                  <div key={t.id} draggable={canDrag}
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify({ type: "tour", id: t.id, cur: dIso }))}
                    data-testid={`cal-tour-${t.code}`}
                    className="rounded-lg bg-lm-brown text-lm-cream px-2 py-1.5 text-xs cursor-grab active:cursor-grabbing">
                    <p className="font-bold flex items-center gap-1"><RouteIcon className="w-3 h-3" /> {t.code}</p>
                    <p className="opacity-90 flex items-center gap-1" data-testid={`cal-tour-departure-${t.code}`}><Clock className="w-3 h-3" /> Partenza {tourDeparture(t)}</p>
                    <p className="opacity-80">{t.stops?.length || 0} tappe · {t.driver?.name?.split(" ")[0] || "n.d."}</p>
                  </div>
                ))}
                {reqByDay(dIso).map((r) => (
                  <div key={r.id} draggable={canDrag}
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify({ type: "request", id: r.id, cur: dIso }))}
                    data-testid={`cal-req-${r.code}`}
                    className="rounded-lg bg-white border border-lm-sandsoft px-2 py-1.5 text-xs cursor-grab active:cursor-grabbing">
                    <p className="font-bold text-lm-brown flex items-center gap-1"><ClipboardList className="w-3 h-3" /> {r.code}</p>
                    <p className="text-lm-text2 truncate">{OPERATION_TYPES[r.operation_type]} · {r.supplier_name || r.origin_address || r.dest_address || "—"}</p>
                    <p className="text-lm-muted truncate">{r.requester_name || ""}</p>
                  </div>
                ))}
                {tourByDay(dIso).length === 0 && reqByDay(dIso).length === 0 && (
                  <p className="text-[11px] text-lm-muted text-center pt-4">—</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {undated.length > 0 && (
        <div className="lm-card p-4">
          <p className="font-semibold text-lm-brown mb-2 text-sm">Senza data ({undated.length}) — trascina su un giorno per assegnare la scadenza</p>
          <div className="flex flex-wrap gap-2">
            {undated.map((r) => (
              <div key={r.id} draggable={canDrag}
                onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify({ type: "request", id: r.id, cur: null }))}
                data-testid={`cal-undated-${r.code}`}
                className="rounded-lg bg-white border border-lm-sandsoft px-2.5 py-1.5 text-xs cursor-grab active:cursor-grabbing">
                <span className="font-bold text-lm-brown">{r.code}</span> · {OPERATION_TYPES[r.operation_type]} · {r.supplier_name || "—"}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
